import React, { useState, useEffect } from 'react';
import './NetworkPage.css';
import { 
  Check,
  X,
  UserPlus,
  Clock,
  Users,
  User,
  MessageCircle,
  Loader2,
} from 'lucide-react';
import axios from 'axios';

const NetworkPage = () => {
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [recentConnections, setRecentConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("requests"); // For mobile tabs
  const [loading, setLoading] = useState({
    received: true,
    suggested: true,
    recent: true
  });

  const token = localStorage.getItem("token");

  const fetchReceivedRequests = async () => {
    try {
      setLoading(prev => ({ ...prev, received: true }));
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/network/requests/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedRequests(response.data);
    } catch (error) {
      console.error('Error fetching received requests:', error);
      setReceivedRequests([]);
    } finally {
      setLoading(prev => ({ ...prev, received: false }));
    }
  };

  const fetchSuggestedUsers = async () => {
    try {
      setLoading(prev => ({ ...prev, suggested: true }));
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Raw suggestions data:', response.data);
      setSuggestedUsers(response.data);
      console.log(response.data);
    } catch (error) {
      console.error('Error fetching suggested users:', error);
      setSuggestedUsers([]);
    } finally {
      setLoading(prev => ({ ...prev, suggested: false }));
    }
  };

  const fetchRecentConnections = async () => {
    try {
      setLoading(prev => ({ ...prev, recent: true }));
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/network/connections/recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentConnections(response.data);
    } catch (error) {
      console.error('Error fetching recent connections:', error);
      setRecentConnections([]);
    } finally {
      setLoading(prev => ({ ...prev, recent: false }));
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/network/requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
      fetchRecentConnections();
      fetchSuggestedUsers();
    } catch (error) {
      console.error('Error accepting request:', error);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/network/requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const handleSendRequest = async (userId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/network/requests/send/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestedUsers(prev => prev.filter(user => user.id !== userId));
      fetchReceivedRequests();
    } catch (error) {
      console.error('Error sending request:', error);
    }
  };

  useEffect(() => {
    fetchReceivedRequests();
    fetchSuggestedUsers();
    fetchRecentConnections();
  }, []);

  const filteredReceived = receivedRequests.filter(req =>
    req.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.user?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.user?.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSuggestions = suggestedUsers.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRecent = recentConnections.filter(connection =>
    connection.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.user?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.user?.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="network-container">
      {/* Header */}
      <header className="network-header">
        <div className="header-content">
          <h1 className="page-title">Network</h1>
          
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="Search connections, skills, companies..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      {/* Mobile Tabs */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <UserPlus size={16} />
          <span>Requests</span>
          {receivedRequests.length > 0 && (
            <span className="tab-badge">{receivedRequests.length}</span>
          )}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'suggestions' ? 'active' : ''}`}
          onClick={() => setActiveTab('suggestions')}
        >
          <Users size={16} />
          <span>Suggestions</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'connections' ? 'active' : ''}`}
          onClick={() => setActiveTab('connections')}
        >
          <Clock size={16} />
          <span>Recent</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="network-content">
        {/* Desktop Grid - 3 columns */}
        <div className="desktop-grid">
          {/* Connection Requests Section */}
          <section className="network-section">
            <div className="section-header">
              <div className="section-title">
                <UserPlus size={18} />
                <h2>Connection Requests</h2>
              </div>
              <span className="section-count">{receivedRequests.length}</span>
            </div>

            <div className="section-body">
              {loading.received ? (
                <div className="loading-state">
                  <Loader2 size={24} className="spin" />
                  <p>Loading requests...</p>
                </div>
              ) : filteredReceived.length > 0 ? (
                <div className="users-list">
                  {filteredReceived.map((request) => (
                    <div key={request.id} className="user-card">
                      <div className="user-avatar">
                        {request.user?.profilepic ? (
                          <img src={`${process.env.REACT_APP_FILE_URL}${request.user.profilepic}`} alt={request.user.name} />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{request.user?.name || 'User'}</h3>
                        <p className="user-headline">
                          {request.user?.title || 'Member'}
                          {request.user?.company && ` at ${request.user.company}`}
                        </p>
                        {request.mutualConnections > 0 && (
                          <p className="mutual-info">
                            {request.mutualConnections} mutual connection{request.mutualConnections !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <div className="user-actions">
                        <button 
                          className="action-btn accept"
                          onClick={() => handleAcceptRequest(request.id)}
                          title="Accept"
                        >
                          <Check size={16} />
                        </button>
                        <button 
                          className="action-btn reject"
                          onClick={() => handleRejectRequest(request.id)}
                          title="Ignore"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="networkempty-state">
                  <UserPlus size={40} />
                  <p>No pending requests</p>
                </div>
              )}
            </div>
          </section>

          {/* Suggestions Section */}
          <section className="network-section">
            <div className="section-header">
              <div className="section-title">
                <Users size={18} />
                <h2>People You May Know</h2>
              </div>
            </div>

            <div className="section-body">
              {loading.suggested ? (
                <div className="loading-state">
                  <Loader2 size={24} className="spin" />
                  <p>Loading suggestions...</p>
                </div>
              ) : filteredSuggestions.length > 0 ? (
                <div className="users-list">
                  {filteredSuggestions.map((user) => (
                    <div key={user.id} className="user-card">
                      <div className="user-avatar">
                        {user.profilepic ? (
                           <img src={`${process.env.REACT_APP_FILE_URL}${user.profilepic}`} alt={user.name} />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{user.name || 'User'}</h3>
                        <p className="user-headline">
                          {user.title || 'Professional'}
                          {user.company && ` at ${user.company}`}
                        </p>
                        {user.mutualConnections > 0 && (
                          <p className="mutual-info">
                            {user.mutualConnections} mutual connection{user.mutualConnections !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <button 
                        className="connect-btn"
                        onClick={() => handleSendRequest(user.id)}
                      >
                        <UserPlus size={14} />
                        <span>Connect</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="networkempty-state">
                  <Users size={40} />
                  <p>No suggestions available</p>
                </div>
              )}
            </div>
          </section>

          {/* Recent Connections Section */}
          <section className="network-section">
            <div className="section-header">
              <div className="section-title">
                <Clock size={18} />
                <h2>Recent Connections</h2>
              </div>
              <span className="section-count">{recentConnections.length}</span>
            </div>

            <div className="section-body">
              {loading.recent ? (
                <div className="loading-state">
                  <Loader2 size={24} className="spin" />
                  <p>Loading connections...</p>
                </div>
              ) : filteredRecent.length > 0 ? (
                <div className="users-list">
                  {filteredRecent.map((connection) => (
                    <div key={connection.id} className="user-card">
                      <div className="user-avatar connected">
                        {connection.user?.profilepic ? (
                          <img src={`${process.env.REACT_APP_FILE_URL}${connection.user.profilepic}`} alt={connection.user.name} />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{connection.user?.name || 'User'}</h3>
                        <p className="user-headline">
                          {connection.user?.title || 'Member'}
                          {connection.user?.company && ` at ${connection.user.company}`}
                        </p>
                        <p className="connection-time">
                          Connected {new Date(connection.connectedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button className="message-btn">
                        <MessageCircle size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="networkempty-state">
                  <Clock size={40} />
                  <p>No recent connections</p>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Mobile Content - Single column with tabs */}
        <div className="mobile-content">
          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <section className="mobile-section">
              <div className="users-list">
                {loading.received ? (
                  <div className="loading-state">
                    <Loader2 size={24} className="spin" />
                    <p>Loading requests...</p>
                  </div>
                ) : filteredReceived.length > 0 ? (
                  filteredReceived.map((request) => (
                    <div key={request.id} className="user-card mobile">
                      <div className="user-avatar">
                        {request.user?.profilepic ? (
                          <img src={`${process.env.REACT_APP_FILE_URL}${request.user.profilepic}`} alt={request.user.name} />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{request.user?.name || 'User'}</h3>
                        <p className="user-headline">
                          {request.user?.title || 'Member'}
                          {request.user?.company && ` at ${request.user.company}`}
                        </p>
                        {request.mutualConnections > 0 && (
                          <p className="mutual-info">
                            {request.mutualConnections} mutual
                          </p>
                        )}
                      </div>
                      <div className="user-actions mobile">
                        <button 
                          className="action-btn accept"
                          onClick={() => handleAcceptRequest(request.id)}
                        >
                          <Check size={18} />
                        </button>
                        <button 
                          className="action-btn reject"
                          onClick={() => handleRejectRequest(request.id)}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="networkempty-state">
                    <UserPlus size={40} />
                    <p>No pending requests</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Suggestions Tab */}
          {activeTab === 'suggestions' && (
            <section className="mobile-section">
              <div className="users-list">
                {loading.suggested ? (
                  <div className="loading-state">
                    <Loader2 size={24} className="spin" />
                    <p>Loading suggestions...</p>
                  </div>
                ) : filteredSuggestions.length > 0 ? (
                  filteredSuggestions.map((user) => (
                    <div key={user.id} className="user-card mobile">
                      <div className="user-avatar">
                        {user.profilepic ? (
                          <img src={`${process.env.REACT_APP_FILE_URL}${user.profilepic}`} alt={user.name} />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{user.name || 'User'}</h3>
                        <p className="user-headline">
                          {user.title || 'Professional'}
                          {user.company && ` at ${user.company}`}
                        </p>
                        {user.mutualConnections > 0 && (
                          <p className="mutual-info">
                            {user.mutualConnections} mutual
                          </p>
                        )}
                      </div>
                      <button 
                        className="connect-btn mobile"
                        onClick={() => handleSendRequest(user.id)}
                      >
                        <UserPlus size={16} />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="networkempty-state">
                    <Users size={40} />
                    <p>No suggestions available</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <section className="mobile-section">
              <div className="users-list">
                {loading.recent ? (
                  <div className="loading-state">
                    <Loader2 size={24} className="spin" />
                    <p>Loading connections...</p>
                  </div>
                ) : filteredRecent.length > 0 ? (
                  filteredRecent.map((connection) => (
                    <div key={connection.id} className="user-card mobile">
                      <div className="user-avatar connected">
                        {connection.user?.profilepic ? (
                          <img src={`${process.env.REACT_APP_FILE_URL}${connection.user.profilepic}`} alt={connection.user.name} />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{connection.user?.name || 'User'}</h3>
                        <p className="user-headline">
                          {connection.user?.title || 'Member'}
                          {connection.user?.company && ` at ${connection.user.company}`}
                        </p>
                      </div>
                      <button className="message-btn mobile">
                        <MessageCircle size={18} />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="networkempty-state">
                    <Clock size={40} />
                    <p>No recent connections</p>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkPage;